/**
 * ============================================
 * Routing Guide (Lazy Load + ProtectedRoute)
 * ============================================
 *
 * Pages are auto-generated in `PageMap.js` using a Node.js script (npm run generate:pages).
 *    Each page is lazy-loaded for better performance (code splitting).
 *
 * How to use a page:
 *    import { PageMap } from '@/routes/PageMap';
 *    element: <PageMap.PageName />
 *
 * How to protect a route:
 *    Wrap the element in <ProtectedRoute> to enforce permissions/roles.
 *
 *    Example:
 *    <Route
 *      path="/your-page"
 *      element={
 *        <ProtectedRoute
 *          action="access"
 *          subject="module"
 *          requiredPermissions={['is_admin_access']}
 *        >
 *          <PageMap.YourPage />
 *        </ProtectedRoute>
 *      }
 *    />
 *
 * To add a new page:
 *    1. Create the component inside `src/pages/`
 *    2. Run the script: `npm run generate:pages`
 *    3. Use it via `PageMap.NewPage`
 *
 * <ProtectedRoute /> props:
 *    - action: what the user is trying to do (e.g., 'view', 'edit', 'access')
 *    - subject: the module/resource (e.g., 'contact', 'user')
 *    - requiredPermissions: optional, array of flags like ['is_admin_access']
 *
 * Notes:
 *    - `PageMap` enables dynamic, lazy-loaded imports
 *    - `ProtectedRoute` helps with CASL-based access control
 */

import { Navigate, Route, Routes } from 'react-router';
import {
  AuthPage,
  canAccessBillingSummary,
  canAccessCompanyDetail,
  canAccessCompanyInvoicePreview,
  canAccessCompanyPackages,
  canAccessCompanySettings,
  useAuthContext
} from '@/auth';
import { PublicPage } from '@/public';
import { PortalPage } from '@/portal';
import { RequireAuth } from '@/auth/RequireAuth';
import { MainLayout } from '@/layouts/main';
import { ErrorsRouting } from '@/errors';
import ProtectedRoute from '@/hooks/ProtectedRoute';
import { PageMap } from '@/pages/PageMap';

const AppRoutingSetup = () => {
  const { auth } = useAuthContext();
  const user = auth?.user;

  return <Routes>
    <Route element={<RequireAuth />}>
      <Route element={<MainLayout />}>
        <Route path="/" element={
          <PageMap.DefaultPage />
        } />
        {/* ============= Contact Module Pages ============ */}
        <Route path="/contacts" element={
          <ProtectedRoute action="access" subject="contact" requiredPermissions={['is_admin_access']}>
            <PageMap.Contacts />
          </ProtectedRoute>
        } />
        <Route path="/contacts/:id" element={
          <ProtectedRoute action="access" subject="contact" requiredPermissions={['is_admin_access']}>
            <PageMap.ContactsDetail />
          </ProtectedRoute>
        } />
        <Route path="/contacts/create" element={
          <ProtectedRoute action="handle" subject="contact" requiredPermissions={['is_admin_access']}>
            <PageMap.AddContacts />
          </ProtectedRoute>
        } />
        <Route path="/contacts/edit/:id" element={
          <ProtectedRoute action="handle" subject="contact" requiredPermissions={['is_admin_access']}>
            <PageMap.AddContacts />
          </ProtectedRoute>
        } />
        <Route path="/contacts/contact-type" element={
          <ProtectedRoute action="handle" subject="contact" requiredPermissions={['is_admin_access']}>
            <PageMap.ContactType />
          </ProtectedRoute>
        } />
        <Route path="/contacts/specialities" element={
          <ProtectedRoute action="handle" subject="contact" requiredPermissions={['is_admin_access']}>
            <PageMap.Specialities />
          </ProtectedRoute>
        } />

        {/* ============= Matters Module Pages ============= */}
        <Route path="/matters" element={
          <ProtectedRoute action="access" subject="matter" requiredPermissions={['is_admin_access']}>
            <PageMap.Matters />
          </ProtectedRoute>
        } />
        <Route path="/matters/:action/:id?" element={
          <ProtectedRoute action="handle" subject="matter" requiredPermissions={['is_admin_access']}>
            <PageMap.AddMatters />
          </ProtectedRoute>
        } />
        <Route path="/matters/detail/:id" element={
          <ProtectedRoute action="access" subject="matter" requiredPermissions={['is_admin_access']}>
            <PageMap.MattersDetail />
          </ProtectedRoute>
        } />
        <Route path="/matters/archive-matters" element={<PageMap.ArchiveMatters />} />

        {/* ============= Reminders ============= */}
        <Route path="/reminders" element={
          <ProtectedRoute action="access" subject="reminder">
            <PageMap.Reminders />
          </ProtectedRoute>
        } />

        {/* ============= Shared Storage ============= */}
        <Route path="/shared-storage" element={
          <ProtectedRoute action="access" subject="shared-storage">
            <PageMap.SharedStorageList />
          </ProtectedRoute>
        } />
        <Route path="/shared-storage/:id" element={
          <ProtectedRoute action="access" subject="shared-storage">
            <PageMap.SharedStorageDetail />
          </ProtectedRoute>
        } />

        {/* ============= Client Portal guests (OL-194) ============= */}
        <Route path="/client-portal/guests" element={
          <ProtectedRoute action="share" subject="matter-files">
            <PageMap.ClientPortalGuests />
          </ProtectedRoute>
        } />

        {/* ============= Basic Setup (Custom Fields, Tags) ============= */}
        <Route path="/account/home/custom-fields" element={<PageMap.CustomFields />} />
        <Route path="/account/home/record-types" element={
          <ProtectedRoute action="access" subject="matter" requiredPermissions={['is_admin_access']}>
            <PageMap.RecordTypes />
          </ProtectedRoute>
        } />
        <Route path="/account/home/record-types/:id" element={
          <ProtectedRoute action="access" subject="matter" requiredPermissions={['is_admin_access']}>
            <PageMap.RecordTypeLayoutEditor />
          </ProtectedRoute>
        } />
        <Route path="/account/home/tags" element={<PageMap.Tags />} />
        <Route path="/recycle-bin" element={<PageMap.RecycleBinList />} />
        {/* Export approval (OL-154): staff see their own requests, approvers see all. */}
        <Route path="/account/export-requests" element={<PageMap.ExportRequests />} />
        {/* Firm-wide audit log (OL-156), administrators only. */}
        <Route path="/account/audit-log" element={
          <ProtectedRoute requiredPermissions={['is_admin_access']}>
            <PageMap.AuditLog />
          </ProtectedRoute>
        } />
        {/* <Route path="/account/home/map-fields" element={<PageMap.MappedFields />} /> */}

        {/* ============= Invoice Module Pages ============= */}
        <Route path="/billing/invoices/invoice" element={
          <ProtectedRoute action="access" subject="invoice" requiredPermissions={['is_admin_access']}>
            <PageMap.Invoice />
          </ProtectedRoute>
        } />
        <Route path="/billing/invoice/preview/:id" element={
          <ProtectedRoute action="access" subject="invoice" requiredPermissions={['is_admin_access']}>
            <PageMap.InvoiceDetailsPreview />
          </ProtectedRoute>
        } />
        <Route path="/billing/invoice/:action/:id?" element={
          <ProtectedRoute action="handle" subject="invoice" requiredPermissions={['is_admin_access']}>
            <PageMap.AddInvoice />
          </ProtectedRoute>
        } />
        <Route path="/billing/invoice/invoice-template" element={
          <ProtectedRoute action="access" subject="invoice" requiredPermissions={['is_admin_access']}>
            <PageMap.InvoiceTemplate />
          </ProtectedRoute>
        } />
        <Route path="/billing/invoice/template/create" element={
          <ProtectedRoute action="handle" subject="invoice" requiredPermissions={['is_admin_access']}>
            <PageMap.AddInvoiceTemplate />
          </ProtectedRoute>
        } />
        <Route path="/billing/invoice/template/edit/:id" element={
          <ProtectedRoute action="handle" subject="invoice" requiredPermissions={['is_admin_access']}>
            <PageMap.AddInvoiceTemplate />
          </ProtectedRoute>
        } />
        {/* <Route path="/billing/invoice/:id" element={
            <ProtectedRoute action="access" subject="invoice" requiredPermissions={['is_admin_access']}>
              <InvoiceDetails />
            </ProtectedRoute>
          }/> */}


        {/* ============== Expense Module Pages ============= */}
        <Route path="/billing/expense/category" element={
          <ProtectedRoute action="handle" subject="expense" requiredPermissions={['is_admin_access']}>
            <PageMap.Category />
          </ProtectedRoute>
        } />
        <Route path="/billing/expense" element={
          <ProtectedRoute action="handle" subject="expense" requiredPermissions={['is_admin_access']}>
            <PageMap.Expense />
          </ProtectedRoute>
        } />
        {/* <Route path="/billing/expense/create/:id?" element={
            <ProtectedRoute action="handle" subject="expense" requiredPermissions={['is_admin_access']}>
              <AddExpense />
            </ProtectedRoute>
          }/>
          <Route path="/billing/expense/edit/:id" element={
            <ProtectedRoute action="handle" subject="expense" requiredPermissions={['is_admin_access']}>
              <EditExpense />
            </ProtectedRoute>
          }/> */}


        {/* ============= Tax Module Pages ============= */}
        <Route path="/billing/taxes" element={
          <ProtectedRoute action="handle" subject="tax" requiredPermissions={['is_admin_access']}>
            <PageMap.Taxes />
          </ProtectedRoute>
        } />

        {/* ============= Items Module Pages ============= */}
        <Route path="/billing/items" element={
          <ProtectedRoute action="handle" subject="items" requiredPermissions={['is_admin_access']}>
            <PageMap.Items />
          </ProtectedRoute>
        } />

        {/* ============= Flat Fees Module Pages ============= */}
        <Route path="/billing/flat-fees" element={
          <ProtectedRoute action="handle" subject="flat-fees" requiredPermissions={['is_admin_access']}>
            <PageMap.FlatFees />
          </ProtectedRoute>
        } />
        {/* <Route path="/billing/flat-fees/create/:id?" element={
            <ProtectedRoute action="handle" subject="flat-fees" requiredPermissions={['is_admin_access']}>
              <AddFlatFees />
            </ProtectedRoute>
          }/>
          <Route path="/billing/flat-fees/edit/:id?" element={
            <ProtectedRoute action="handle" subject="flat-fees" requiredPermissions={['is_admin_access']}>
              <EditFlatFees />
            </ProtectedRoute>
          }/> */}

        {/* ============= Role Module Pages ============= */}
        <Route path="/account/members/roles" element={
          <ProtectedRoute action="handle" subject="role" requiredPermissions={['is_admin_access']}>
            <PageMap.AccountRolesPage />
          </ProtectedRoute>
        } />


        {/* ============= Staff/User Module Pages ============= */}
        <Route path="/account/members/users" element={
          <ProtectedRoute action="handle" subject="staff" requiredPermissions={['is_admin_access']}>
            <PageMap.AccountUsersPage />
          </ProtectedRoute>
        } />
        <Route path="/account/members/archive-users" element={
          <ProtectedRoute action="handle" subject="staff" requiredPermissions={['is_admin_access']}>
            <PageMap.ArchiveUsers />
          </ProtectedRoute>
        } />

        {/* ============= User Profile/Security/Company Settings Module Pages ============= */}
        <Route path="/account/home/user-profile" element={<PageMap.AccountUserProfilePage />} />
        <Route path="/account/security/overview" element={<PageMap.AccountOverviewPage />} />
        <Route path="/account/home/company-settings" element={
          <ProtectedRoute requiredPermissions={['is_admin_access']}>
            <PageMap.CompanySettings />
          </ProtectedRoute>
        } />
        <Route path="/account/global/settings/?" element={
          <ProtectedRoute allow={canAccessCompanySettings(user)}>
            <PageMap.AccountSettingsSidebarPage />
          </ProtectedRoute>
        } />

        {/* ============== Referral Module Pages ============= */}
        <Route path="/account/home/referral-request" element={
          <ProtectedRoute action="handle" subject="referral-request" requiredPermissions={['is_admin_access']}>
            <PageMap.ReferralRequest />
          </ProtectedRoute>
        } />
        <Route path="/account/home/referral-attorney" element={
          <PageMap.ReferralAttorney />
        } />
        <Route path="/account/home/referred-contact-list" element={
          <ProtectedRoute action="handle" subject="referral-contact" requiredPermissions={['is_admin_access']}>
            <PageMap.ReferredContact />
          </ProtectedRoute>
        } />
        <Route path="/account/home/attorney-referred-contact-list" element={
          <ProtectedRoute action="handle" subject="attorney" requiredPermissions={['is_admin_access']}>
            <PageMap.ReferralRequestAttorney />
          </ProtectedRoute>
        } />




        {/* ============= Mass-Mailing (Email Signature) Module Pages ============= */}
        <Route path="/mass-mailing/signature" element={
          <ProtectedRoute action="access" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.Signature />
          </ProtectedRoute>
        } />


        {/* ============= Mass-Mailing (Email Template) Module Pages ============= */}
        <Route path="/mass-mailing/template-design" element={
          <ProtectedRoute action="access" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.TemplateDesign />
          </ProtectedRoute>
        } />
        <Route path="/mass-mailing/template-design/view/:id" element={
          <ProtectedRoute action="access" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.ViewTemplateDesign />
          </ProtectedRoute>
        } />
        <Route path="/mass-mailing/template-design/create/:id?" element={
          <ProtectedRoute action="handle" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.AddTemplateDesign />
          </ProtectedRoute>
        } />

        {/* ============= Mass-Mailing (Subscription) Module Pages ============= */}
        <Route path="/mass-mailing/subscriptions" element={
          <ProtectedRoute action="access" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.SubscriptionList />
          </ProtectedRoute>
        } />
        <Route path="/mass-mailing/subscription-form/create/:id?" element={
          <ProtectedRoute action="handle" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.SubscriptionForm />
          </ProtectedRoute>
        } />
        <Route path="/mass-mailing/subscription-form/edit/:id" element={
          <ProtectedRoute action="handle" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.SubscriptionForm />
          </ProtectedRoute>
        } />

        {/* ============= Mass-Mailing (Groups) Module Pages ============= */}
        <Route path="/mass-mailing/groups" element={
          <ProtectedRoute action="access" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.CrmGroup />
          </ProtectedRoute>
        } />
        <Route path="/mass-mailing/groups/create" element={
          <ProtectedRoute action="handle" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.GroupEditor />
          </ProtectedRoute>
        } />
        <Route path="/mass-mailing/groups/:id" element={
          <ProtectedRoute action="access" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.GroupEditor />
          </ProtectedRoute>
        } />

        {/* ============= Mass-Mailing (Email Metrics) Module Pages ============= */}
        <Route path="/mass-mailing/metrics/:id?" element={
          <ProtectedRoute action="access" subject="mass-mailing" requiredPermissions={['is_admin_access']}>
            <PageMap.MassMailDashboard />
          </ProtectedRoute>
        } />

        {/* ============= Notification Module Pages ============= */}
        <Route path="/notifications/all" element={
          <ProtectedRoute action="access" subject="notification" requiredPermissions={['is_admin_access']}>
            <PageMap.AllNotification />
          </ProtectedRoute>
        } />

        {/* ============= Reports Module Page ============= */}
        {/* ============= (Case Counts) Module Page ============= */}
        <Route path="/reports/case-counts" element={
          <ProtectedRoute action="access" subject="reports" requiredPermissions={['is_admin_access']}>
            <PageMap.CaseCountReport />
          </ProtectedRoute>
        } />
        {/* ============= (AR Report) Module Page ============= */}
        <Route path="/reports/ar-report" element={
          <ProtectedRoute action="access" subject="ar-reports" requiredPermissions={['is_admin_access']}>
            <PageMap.ArReport />
          </ProtectedRoute>
        } />
        {/* ============= (SignatureReport) Module Page ============= */}
        <Route path="/documents/e-signature-documents" element={
          <ProtectedRoute action="access" subject="documents" requiredPermissions={['is_admin_access']}>
            <PageMap.SignatureReport />
          </ProtectedRoute>
        } />
        {/* ============= (Raw Data Report) Module Page ============= */}
        <Route path="/reports/raw-data-report" element={
          <ProtectedRoute action="access" subject="raw-data-reports" requiredPermissions={['is_admin_access']}>
            <PageMap.Dashboard />
          </ProtectedRoute>
        } />
        {/* ============= (ADHOC Report) Module Page ============= */}
        <Route path="/reports/adhoc-report" element={
          <ProtectedRoute action="access" subject="adhoc-reports" requiredPermissions={['is_admin_access']}>
            <PageMap.AdhocDashboard />
          </ProtectedRoute>
        } />

        {/* ============= (Intake Form) Module Page ============= */}
        <Route path="/intake-forms" element={
          <ProtectedRoute action="access" subject="intake-form" requiredPermissions={['is_admin_access']}>
            <PageMap.IntakeForms />
          </ProtectedRoute>
        } />
        <Route path="/intake-forms/create" element={
          <ProtectedRoute action="handle" subject="intake-form" requiredPermissions={['is_admin_access']}>
            <PageMap.CreateIntakeForm />
          </ProtectedRoute>
        } />
        <Route path="/intake-forms/preview/:id" element={
          <ProtectedRoute action="access" subject="intake-form" requiredPermissions={['is_admin_access']}>
            <PageMap.IntakeFormPreview />
          </ProtectedRoute>
        } />
        <Route path="/intake-forms/edit/:id" element={
          <ProtectedRoute action="handle" subject="intake-form" requiredPermissions={['is_admin_access']}>
            <PageMap.CreateIntakeForm />
          </ProtectedRoute>
        } />

        {/* ============= (Intake Form) Module Page ============= */}
        <Route path="/data-import/import" element={
          <ProtectedRoute action="handle" subject="import" requiredPermissions={['is_admin_access']}>
            <PageMap.DataImport />
          </ProtectedRoute>
        } />
        <Route path="/data-import/details" element={
          <ProtectedRoute action="access" subject="import" requiredPermissions={['is_admin_access']}>
            <PageMap.DataImportDetails />
          </ProtectedRoute>
        } />


        {/* ============ Letter templates + demand letters (OL-119) ============= */}
        <Route path="/documents/letter-templates" element={
          <ProtectedRoute action="access" subject="letter-template" requiredPermissions={['is_admin_access']}>
            <PageMap.LetterTemplates />
          </ProtectedRoute>
        } />
        <Route path="/documents/letter-templates/create" element={
          <ProtectedRoute action="access" subject="letter-template" requiredPermissions={['is_admin_access']}>
            <PageMap.LetterTemplateEditor />
          </ProtectedRoute>
        } />
        <Route path="/documents/demand-letters" element={
          <ProtectedRoute action="access" subject="demand-letter" requiredPermissions={['is_admin_access']}>
            <PageMap.DemandLetters />
          </ProtectedRoute>
        } />
        <Route path="/documents/letter-templates/:id" element={
          <ProtectedRoute action="access" subject="letter-template" requiredPermissions={['is_admin_access']}>
            <PageMap.LetterTemplateEditor />
          </ProtectedRoute>
        } />
        <Route path="/demand-letters/:id" element={
          <ProtectedRoute action="access" subject="demand-letter" requiredPermissions={['is_admin_access']}>
            <PageMap.DemandLetterEditor />
          </ProtectedRoute>
        } />

        {/* ============ (Documents) Module Page ============= */}
        <Route path="/documents/document-templates" element={
          <ProtectedRoute action="access" subject="document-template" requiredPermissions={['is_admin_access']}>
            <PageMap.DocumentTemplate />
          </ProtectedRoute>
        } />

        {/* ============ Company - Billing Module Page ============= */}
        <Route path="/company/list" element={
          <PageMap.CompaniesList />
        } />
        <Route path="/company-billing/license" element={
          <PageMap.Packages />
        } />
        <Route path="/billing/summary" element={
          canAccessBillingSummary(user) ? <PageMap.PlanUsage /> : <Navigate to="/" replace />
        } />
        <Route path="/billing/summary/invoice-preview/:id?" element={
          // canAccessBillingSummary(user) ? <PageMap.PlanUsageInvoicePreview /> : <Navigate to="/" replace />
          canAccessBillingSummary(user) ? <PageMap.CompanyInvoicePreview /> : <Navigate to="/" replace />
        } />
        <Route path="/company/detail/:id?" element={
          canAccessCompanyDetail(user) ? <PageMap.CompanyDetail /> : <Navigate to="/" replace />
        } />
        <Route path="/company/detail/invoice-preview/:id?" element={
          canAccessCompanyInvoicePreview(user) ? <PageMap.CompanyInvoicePreview /> : <Navigate to="/" replace />
        } />

        <Route path="/licenses" element={
          canAccessCompanyPackages(user) ? <PageMap.CompanyPackage /> : <Navigate to="/" replace />
        } />

        <Route path="/discount-code" element={
          canAccessCompanyPackages(user) ? <PageMap.CompanyCoupons /> : <Navigate to="/" replace />
        } />
        <Route path="/discount-code-usage" element={
          canAccessCompanyPackages(user) ? <PageMap.CompanyCouponsUsage /> : <Navigate to="/" replace />
        } />

        {/* ============= Extras Module Pages ============= */}
        <Route path="/account/home/settings-sidebar" element={<PageMap.AccountSettingsSidebarPage />} />
        {/* <Route path="/dark-sidebar" element={<PageMap.Demo1DarkSidebarPage />} />
        <Route path="/public-profile/profiles/default" element={<PageMap.ProfileDefaultPage />} />
        <Route path="/public-profile/profiles/creator" element={<PageMap.ProfileCreatorPage />} />
        <Route path="/public-profile/profiles/company" element={<PageMap.ProfileCompanyPage />} />
        <Route path="/public-profile/profiles/nft" element={<PageMap.ProfileNFTPage />} /> 
        <Route path="/public-profile/profiles/blogger" element={<PageMap.ProfileBloggerPage />} /> 
        <Route path="/public-profile/profiles/crm" element={<PageMap.ProfileCRMPage />} /> 
        <Route path="/public-profile/profiles/gamer" element={<PageMap.ProfileGamerPage />} />
        <Route path="/public-profile/profiles/feeds" element={<PageMap.ProfileFeedsPage />} />
        <Route path="/public-profile/profiles/plain" element={<PageMap.ProfilePlainPage />} />
        <Route path="/public-profile/profiles/modal" element={<PageMap.ProfileModalPage />} />
        <Route path="/public-profile/projects/3-columns" element={<PageMap.ProjectColumn3Page />} />
        <Route path="/public-profile/projects/2-columns" element={<PageMap.ProjectColumn2Page />} />
        <Route path="/public-profile/works" element={<PageMap.ProfileWorksPage />} />
        <Route path="/public-profile/teams" element={<PageMap.ProfileTeamsPage />} />
        <Route path="/public-profile/network" element={<PageMap.ProfileNetworkPage />} />
        <Route path="/public-profile/activity" element={<PageMap.ProfileActivityPage />} /> 
        <Route path="/public-profile/campaigns/card" element={<PageMap.CampaignsCardPage />} />
        <Route path="/public-profile/campaigns/list" element={<PageMap.CampaignsListPage />} />
        <Route path="/public-profile/empty" element={<PageMap.ProfileEmptyPage />} /> 
        <Route path="/account/home/get-started" element={<PageMap.AccountGetStartedPage />} />
        <Route path="/account/home/company-profile" element={<PageMap.AccountCompanyProfilePage />} /> 
        <Route path="/account/home/settings-enterprise" element={<PageMap.AccountSettingsEnterprisePage />} />
        <Route path="/account/home/settings-plain" element={<PageMap.AccountSettingsPlainPage />} />
        <Route path="/account/home/settings-modal" element={<PageMap.AccountSettingsModalPage />} />
        <Route path="/account/billing/basic" element={<PageMap.AccountBasicPage />} />
        <Route path="/account/billing/enterprise" element={<PageMap.AccountEnterprisePage />} />
        <Route path="/account/billing/plans" element={<PageMap.AccountPlansPage />} />
        <Route path="/account/billing/history" element={<PageMap.AccountHistoryPage />} /> 
        <Route path="/account/security/get-started" element={<PageMap.AccountSecurityGetStartedPage />} />
        <Route path="/account/security/allowed-ip-addresses" element={<PageMap.AccountAllowedIPAddressesPage />} />
        <Route path="/account/security/privacy-settings" element={<PageMap.AccountPrivacySettingsPage />} />
        <Route path="/account/security/device-management" element={<PageMap.AccountDeviceManagementPage />} />
        <Route path="/account/security/backup-and-recovery" element={<PageMap.AccountBackupAndRecoveryPage />} />
        <Route path="/account/security/current-sessions" element={<PageMap.AccountCurrentSessionsPage />} />
        <Route path="/account/security/security-log" element={<PageMap.AccountSecurityLogPage />} />
        <Route path="/account/members/team-starter" element={<PageMap.AccountTeamsStarterPage />} />
        <Route path="/account/members/teams" element={<PageMap.AccountTeamsPage />} />
        <Route path="/account/members/teams/create" element={<PageMap.AccountTeamsPage />} />
        <Route path="/account/members/team-info" element={<PageMap.AccountTeamInfoPage />} />
        <Route path="/account/members/members-starter" element={<PageMap.AccountMembersStarterPage />} />
        <Route path="/account/members/team-members" element={<PageMap.AccountTeamMembersPage />} />
        <Route path="/account/members/import-members" element={<PageMap.AccountImportMembersPage />} />
        <Route path="/account/members/permissions-toggle" element={<PageMap.AccountPermissionsTogglePage />} />
        <Route path="/account/members/permissions-check" element={<PageMap.AccountPermissionsCheckPage />} />  
        <Route path="/account/integrations" element={<PageMap.AccountIntegrationsPage />} />
        <Route path="/account/notifications" element={<PageMap.AccountNotificationsPage />} />
        <Route path="/account/api-keys" element={<PageMap.AccountApiKeysPage />} />
        <Route path="/account/appearance" element={<PageMap.AccountAppearancePage />} />
        <Route path="/account/invite-a-friend" element={<PageMap.AccountInviteAFriendPage />} />
        <Route path="/account/activity" element={<PageMap.AccountActivityPage />} />
        <Route path="/network/get-started" element={<PageMap.NetworkGetStartedPage />} />
        <Route path="/network/user-cards/mini-cards" element={<PageMap.NetworkMiniCardsPage />} />
        <Route path="/network/user-cards/team-crew" element={<PageMap.NetworkUserCardsTeamCrewPage />} />
        <Route path="/network/user-cards/author" element={<PageMap.NetworkAuthorPage />} />
        <Route path="/network/user-cards/nft" element={<PageMap.NetworkNFTPage />} />
        <Route path="/network/user-cards/social" element={<PageMap.NetworkSocialPage />} />
        <Route path="/network/user-table/team-crew" element={<PageMap.NetworkUserTableTeamCrewPage />} />
        <Route path="/network/user-table/app-roster" element={<PageMap.NetworkAppRosterPage />} />
        <Route path="/network/user-table/market-authors" element={<PageMap.NetworkMarketAuthorsPage />} />
        <Route path="/network/user-table/saas-users" element={<PageMap.NetworkSaasUsersPage />} />
        <Route path="/network/user-table/store-clients" element={<PageMap.NetworkStoreClientsPage />} />
        <Route path="/network/user-table/visitors" element={<PageMap.NetworkVisitorsPage />} />
        <Route path="/auth/welcome-message" element={<PageMap.AuthenticationWelcomeMessagePage />} />
        <Route path="/auth/account-deactivated" element={<PageMap.AuthenticationAccountDeactivatedPage />} />
        <Route path="/authentication/get-started" element={<PageMap.AuthenticationGetStartedPage />} /> */}
      </Route>
    </Route>

    {/* ============= Errors Module Pages ============= */}
    <Route path="error/*" element={<ErrorsRouting />} />
    <Route path="*" element={<Navigate to="/error/404" />} />

    {/* ============= Auth Module Pages ============= */}
    <Route path="auth/*" element={<AuthPage />} />

    {/* ============= Public Module Pages ============= */}
    <Route path="public/*" element={<PublicPage />} />

    {/* ============= Client Portal (guests, not users — OL-192) ============= */}
    <Route path="portal/*" element={<PortalPage />} />
  </Routes>;
};
export { AppRoutingSetup };
